"use client";

import BudgetFrequencyField from '@/components/crm/BudgetFrequencyField';
import { budgetLabel, budgetSuffix } from '@/utils/budget';

import { notify, confirmDialog } from "@/components/ui/dialogs";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { 
  Eye, 
  Trash2, 
  FolderOpen,
  Building,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  DollarSign,
  AlertCircle,
  Save,
  Users,
  Edit,
  } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../components/ui/data-table";
import { get, put, del, post, getCurrentOrganization, can } from "../utils/service";
import DealBoard, { type BoardDeal } from "@/components/crm/DealBoard";
import { GridProvider, GridToolbar, StatsStrip, PaginationBar } from "@/components/crm/DataGrid";
import { useCounts, countText } from "@/components/crm/useCounts";
import FloatingDrawer from "@/components/crm/FloatingDrawer";
import AppSelect from "@/components/ui/app-select";
import StatusProgress from "@/components/crm/StatusProgress";
import SearchInput from "@/components/ui/search-input";
import DateInput from "@/components/ui/date-input";

/** A deal, shaped for this page's table and drawer. */
interface Project {
  id: string;
  name: string;
  client: string;
  client_email: string;
  client_phone: string;
  value: string | number | null;
  current_stage: string;
  status: string;
  progress: number;
  created_at: string;
  currency: string;
  budget_frequency?: string;
  owner_name: string;
  account_id: string | null;
  expected_close_date: string | null;
  lost_reason: string | null;
  description: string | null;
}

interface ApiDeal {
  id: string;
  title: string;
  status: string;
  amount: string;
  currency: string;
  budget_frequency?: string;
  probability: number | null;
  effective_probability: number | null;
  expected_close_date: string | null;
  lost_reason: string | null;
  description: string | null;
  stage_id: string;
  account_id: string | null;
  account?: { id: string; name: string } | null;
  contact?: { id: string; name: string; email: string | null; phone?: string | null } | null;
  owner?: { id: string; name: string } | null;
  created_at: string;
}

const fromApi = (d: ApiDeal): Project => ({
  id: d.id,
  name: d.title,
  client: d.account?.name ?? "",
  client_email: d.contact?.email ?? "",
  client_phone: d.contact?.phone ?? "",
  value: d.amount, budget_frequency: d.budget_frequency ?? 'one_time',
  current_stage: d.stage_id,
  status: d.status,
  progress: d.effective_probability ?? d.probability ?? 0,
  created_at: d.created_at,
  currency: d.currency,
  owner_name: d.owner?.name ?? "",
  account_id: d.account_id,
  expected_close_date: d.expected_close_date,
  lost_reason: d.lost_reason,
  description: d.description,
});

interface Stage { id: string; name: string; category: "open" | "won" | "lost"; color: string }

/** The pipeline's stages (loaded once by the page, read by the drawer and modals). */
const StagesContext = createContext<Stage[]>([]);
const STAGE_COLORS = ["bg-blue-100 text-blue-800", "bg-purple-100 text-purple-800", "bg-pink-100 text-pink-800", "bg-orange-100 text-orange-800", "bg-green-100 text-green-800", "bg-cyan-100 text-cyan-800"];

const apiMessage = (error: any, fallback: string) => {
  const errors = error?.response?.data?.errors as Record<string, string[]> | undefined;
  return (errors && Object.values(errors).flat()[0]) || error?.response?.data?.message || fallback;
};

interface ProjectsResponse {
  success: boolean;
  message: string;
  data: ApiDeal[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-red-100 text-red-800",
};

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);


const StageChangeModal = ({ 
  isOpen, 
  onClose, 
  currentStage, 
  newStage, 
  onConfirm 
}: { 
  isOpen: boolean;
  onClose: () => void;
  currentStage: string;
  newStage: string;
  onConfirm: (lostReason?: string) => void;
}) => {
  const stages = useContext(StagesContext);
  const [lostReason, setLostReason] = useState("");
  const getStageName = (stage: string) => {
    return stages.find(s => s.id === stage)?.name || "Unknown";
  };
  const losing = stages.find((s) => s.id === newStage)?.category === "lost";

  useEffect(() => { if (isOpen) setLostReason(""); }, [isOpen]);

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-white">Confirm Stage Change</h3>
            <p className="text-xs text-gray-400 mt-1">Are you sure you want to move this deal?</p>
          </div>
        </div>

        <div className="bg-gray-900 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400 mb-1">Current Stage</p>
              <p className="text-xs font-semibold text-white">{getStageName(currentStage)}</p>
            </div>
            <div className="text-gray-400 text-xs">→</div>
            <div>
              <p className="text-xs text-gray-400 mb-1">New Stage</p>
              <p className="text-xs font-semibold text-primary">{getStageName(newStage)}</p>
            </div>
          </div>
        </div>

        {losing && (
          <div className="mb-6">
            <label className="text-xs font-semibold text-white mb-2 block">Why was this deal lost? *</label>
            <textarea
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              rows={2}
              placeholder="e.g. Chose a competitor, budget cut"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white resize-none"
            />
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(losing ? lostReason : undefined)}
            disabled={losing && !lostReason.trim()}
            className="flex-1 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Change
          </button>
        </div>
      </div>
    </FloatingDrawer>
  );
};

const AddProjectForm = ({ 
  isOpen, 
  onClose,
  onSuccess 
}: { 
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const baseCurrency = getCurrentOrganization()?.base_currency || "UGX";
  const empty = { name: "", client: "", client_email: "", client_phone: "", value: "", budget_frequency: "one_time", currency: baseCurrency };
  const [formData, setFormData] = useState(empty);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  /** Use the account with this exact name if it exists, otherwise create it. */
  const resolveAccount = async (): Promise<string> => {
    const found = await get<{ data: Array<{ id: string; name: string }> }>(`/accounts?search=${encodeURIComponent(formData.client.trim())}&page_size=20`);
    const match = found.data.find((a) => a.name.trim().toLowerCase() === formData.client.trim().toLowerCase());
    if (match) return match.id;
    const created = await post<{ data: { account: { id: string } } }>("/accounts", {
      name: formData.client.trim(), type: "customer", email: formData.client_email || null, phone: formData.client_phone || null,
    });
    return created.data.account.id;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorText("");

    try {
      const accountId = await resolveAccount();
      await post("/deals", { title: formData.name, account_id: accountId, amount: formData.value || 0, budget_frequency: formData.budget_frequency, currency: formData.currency });
      onSuccess();
      onClose();
      setFormData(empty);
    } catch (error) {
      setErrorText(apiMessage(error, "Could not create the deal."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Add New Deal</h2>
          <p className="text-xs text-gray-400 mt-1">Starts in the first stage of your sales pipeline</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Deal Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Account (company) *</label>
          <input
            type="text"
            required
            value={formData.client}
            onChange={(e) => handleChange("client", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Account Email</label>
            <input
              type="email"
              value={formData.client_email}
              onChange={(e) => handleChange("client_email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Account Phone</label>
            <input
              type="tel"
              value={formData.client_phone}
              onChange={(e) => handleChange("client_phone", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <BudgetFrequencyField value={formData.budget_frequency} onChange={(value) => handleChange('budget_frequency', value)} className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-white" labelClassName="text-xs font-semibold text-white mb-2 block" />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">{budgetLabel(formData.budget_frequency)} *</label>
            <input
              type="number"
              required
              value={formData.value}
              onChange={(e) => handleChange("value", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Currency</label>
            <AppSelect
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              <option value="UGX">UGX</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </AppSelect>
          </div>
        </div>

      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        {errorText && <p className="text-xs text-red-400">{errorText}</p>}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.name || !formData.client || !formData.value}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Create Deal
            </>
          )}
        </button>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const EditProjectForm = ({ 
  isOpen, 
  onClose,
  onSuccess,
  project
}: { 
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  project: Project | null;
}) => {
  const [formData, setFormData] = useState({ name: "", value: "", budget_frequency: "one_time", currency: "UGX", expected_close_date: "", description: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        value: project.value !== null && project.value !== undefined ? project.value.toString() : "",
        budget_frequency: project.budget_frequency ?? 'one_time',
        currency: project.currency,
        expected_close_date: project.expected_close_date ?? "",
        description: project.description ?? "",
      });
      setErrorText("");
    }
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project) return;

    setIsSubmitting(true);
    setErrorText("");

    try {
      await put(`/deals/${project.id}`, {
        title: formData.name,
        amount: formData.value || 0,
        budget_frequency: formData.budget_frequency,
        currency: formData.currency,
        expected_close_date: formData.expected_close_date || null,
        description: formData.description || null,
      });
      onSuccess();
      onClose();
    } catch (error) {
      setErrorText(apiMessage(error, "Could not update the deal."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!project) return null;

  const inputClass = "w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white";

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Edit Deal</h2>
          <p className="text-xs text-gray-400 mt-1">{project.client || "Update deal details"}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Deal Name *</label>
          <input type="text" required value={formData.name} onChange={(e) => handleChange("name", e.target.value)} className={inputClass} />
        </div>

        <BudgetFrequencyField value={formData.budget_frequency} onChange={(value) => handleChange('budget_frequency', value)} className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-white" labelClassName="text-xs font-semibold text-white mb-2 block" />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">{budgetLabel(formData.budget_frequency)} *</label>
            <input type="number" required value={formData.value} onChange={(e) => handleChange("value", e.target.value)} className={inputClass} />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Currency</label>
            <AppSelect value={formData.currency} onChange={(e) => handleChange("currency", e.target.value)} className={inputClass}>
              {[...new Set([formData.currency, "UGX", "USD", "EUR", "GBP"])].map((c) => <option key={c} value={c}>{c}</option>)}
            </AppSelect>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Expected Close Date</label>
          <DateInput value={formData.expected_close_date} onChange={(e) => handleChange("expected_close_date", e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Notes</label>
          <textarea rows={3} value={formData.description} onChange={(e) => handleChange("description", e.target.value)} className={`${inputClass} resize-none`} />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        {errorText && <p className="text-xs text-red-400">{errorText}</p>}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Update Deal
            </>
          )}
        </button>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const ProjectDrawer = ({ 
  project, 
  isOpen, 
  onClose,
  onDelete,
  onEdit,
  onStageChange,
}: { 
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (projectId: string) => void;
  onEdit: () => void;
  onStageChange: (projectId: string, stage: string, lostReason?: string) => void;
}) => {
  const stages = useContext(StagesContext);
  const [currentView, setCurrentView] = useState<"details" | "stageConfirm">("details");
  const [selectedStage, setSelectedStage] = useState("");

  useEffect(() => {
    if (isOpen) {
      setCurrentView("details");
      setSelectedStage(project?.current_stage || "");
    }
  }, [isOpen, project]);

  if (!project) return null;

  const handleStageChange = (newStage: string) => {
    setSelectedStage(newStage);
    setCurrentView("stageConfirm");
  };

  const confirmStageChange = (lostReason?: string) => {
    onStageChange(project.id, selectedStage, lostReason);
    setCurrentView("details");
    onClose();
  };

  const formatCurrency = (amount: number | string | null, currency: string = "USD") => {
    if (amount === null || amount === undefined) {
      return `${currency} 0`;
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) {
      return `${currency} 0`;
    }
    if (numAmount >= 1000000000) {
      return `${currency} ${(numAmount / 1000000000).toFixed(1)}B`;
    } else if (numAmount >= 1000000) {
      return `${currency} ${(numAmount / 1000000).toFixed(0)}M`;
    }
    return `${currency} ${numAmount.toLocaleString()}`;
  };

  const renderDetailsView = () => (
    <>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Deal Details</h2>
          <p className="text-xs text-gray-400 mt-1">{project.name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {(() => {
          // The pipeline's open stages in order, ending at its (first) won stage.
          const journey = [...stages.filter((st) => st.category === "open"), ...stages.filter((st) => st.category === "won").slice(0, 1)];
          const currentStage = stages.find((st) => st.id === project.current_stage);
          const lost = currentStage?.category === "lost";
          if (journey.length < 2) return null;
          return (
            <StatusProgress dark
              steps={journey.map((st) => ({ key: st.id, label: st.name }))}
              current={lost ? journey[0].id : project.current_stage}
              exit={lost ? { label: currentStage!.name } : null}
              onSelect={handleStageChange} />
          );
        })()}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Stage</label>
            <AppSelect
              value={project.current_stage}
              onChange={(e) => handleStageChange(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {stages.map(stage => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </AppSelect>
          </div>
          
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Status</label>
            <div className={`px-3 py-2 text-xs font-semibold rounded-lg ${STATUS_COLORS[project.status as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-800'}`}>
              {project.status.toUpperCase()}
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Win Probability</label>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-gray-800 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${project.progress}%` }}
              />
            </div>
            <span className="text-xs font-medium text-white">{project.progress}%</span>
          </div>
        </div>

        {project.status === "lost" && project.lost_reason && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-red-300">Lost</p>
                <p className="text-xs text-red-200 mt-1">{project.lost_reason}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Deal</p>
              <p className="text-xs text-gray-400">{project.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Building className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Account</p>
              <p className="text-xs text-gray-400">{project.client || "—"}</p>
            </div>
          </div>

          {project.client_email && (
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Contact Email</p>
                <p className="text-xs text-gray-400">{project.client_email}</p>
              </div>
            </div>
          )}

          {project.client_phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Contact Phone</p>
                <p className="text-xs text-gray-400">{project.client_phone}</p>
              </div>
            </div>
          )}

          {project.expected_close_date && (
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Expected Close</p>
                <p className="text-xs text-gray-400">{new Date(project.expected_close_date).toLocaleDateString()}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Deal Value</p>
              <p className="text-xs text-gray-400">{formatCurrency(project.value, project.currency)}{budgetSuffix(project.budget_frequency)}</p>
            </div>
          </div>

          {project.owner_name && (
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Owner</p>
                <p className="text-xs text-gray-400">{project.owner_name}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Created</p>
              <p className="text-xs text-gray-400">
                {new Date(project.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {project.description && (
          <div>
            <label className="text-xs font-semibold text-white mb-2 block flex items-center gap-2">
              <Users className="w-4 h-4" />
              Notes
            </label>
            <p className="text-xs text-gray-400 bg-gray-900 p-3 rounded-lg whitespace-pre-line">{project.description}</p>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-gray-800 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onEdit}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          
          <button
            onClick={async () => {
              if (await confirmDialog("Are you sure you want to delete this deal?")) {
                onDelete(project.id);
              }
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <FloatingDrawer
        isOpen={isOpen && currentView !== "stageConfirm"}
        onClose={onClose}
      >
        {renderDetailsView()}
      </FloatingDrawer>

      <StageChangeModal
        isOpen={currentView === "stageConfirm"}
        onClose={() => setCurrentView("details")}
        currentStage={project.current_stage}
        newStage={selectedStage}
        onConfirm={confirmStageChange}
      />
    </>
  );
};

function ActionsCell({ onView, onEdit, onDelete }: { project: Project; onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu isOpen={isOpen} onClose={handleClose} position={position}>
          <TableDropdownItem onClick={() => { onView(); handleClose(); }}>
            <Eye className="w-3 h-3" />
            View Details
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onEdit(); handleClose(); }}>
            <Edit className="w-3 h-3" />
            Edit Deal
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onDelete(); handleClose(); }} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-3 h-3" />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

/** Real deal counts for the list's statistics strip. */
function DealStats({ refreshKey, today, in30 }: { refreshKey: number; today: string; in30: string }) {
  const { counts, loading } = useCounts("/deals", {
    open: { status: "open" }, won: { status: "won" }, lost: { status: "lost" }, closing: { status: "open", close_from: today, close_to: in30 },
  }, refreshKey);
  return (
    <StatsStrip loading={loading} items={[
      { label: "Open deals", value: countText(counts.open) },
      { label: "Closing in 30 days", value: countText(counts.closing), tone: counts.closing ? "warn" : "default" },
      { label: "Won", value: countText(counts.won), tone: "good" },
      { label: "Lost", value: countText(counts.lost), tone: counts.lost ? "bad" : "default" },
    ]} />
  );
}

function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [sort, setSort] = useState("-created_at");
  useEffect(() => { const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300); return () => clearTimeout(t); }, [searchInput]);
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10);
  const [stages, setStages] = useState<Stage[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showProjectDrawer, setShowProjectDrawer] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [view, setView] = useState<"board" | "list">(() => {
    try { return localStorage.getItem("deals_view") === "list" ? "list" : "board"; } catch { return "board"; }
  });
  const [boardKey, setBoardKey] = useState(0);
  const [pendingMove, setPendingMove] = useState<{ id: string; from: string; to: string } | null>(null);
  const chooseView = (v: "board" | "list") => {
    setView(v);
    try { localStorage.setItem("deals_view", v); } catch { /* per-viewer convenience only */ }
  };
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 15,
    total: 0,
    total_pages: 0,
  });

  // Stages of the default sales pipeline, in order (open stages first, then won / lost).
  useEffect(() => {
    get<{ data: { pipelines: Array<{ id: string; is_default: boolean; stages: Array<{ id: string; name: string; category: Stage["category"]; position: number; is_active: boolean }> }> } }>("/pipelines")
      .then((r) => {
        const pipeline = r.data.pipelines.find((p) => p.is_default) ?? r.data.pipelines[0];
        const list = (pipeline?.stages ?? []).filter((st) => st.is_active).sort((a, b) => a.position - b.position);
        setStages(list.map((st, i) => ({
          id: st.id, name: st.name, category: st.category,
          color: st.category === "won" ? "bg-green-100 text-green-800" : st.category === "lost" ? "bg-red-100 text-red-800" : STAGE_COLORS[i % STAGE_COLORS.length],
        })));
      })
      .catch(() => setStages([]));
  }, []);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get<ProjectsResponse>("/deals", {
        page: pagination.page,
        page_size: pagination.per_page,
        stage_id: stageFilter !== "all" ? stageFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        search: searchQuery || undefined,
        sort,
      });
      if (response.success) {
        setProjects(response.data.map(fromApi));
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total ?? 0,
          total_pages: response.pagination?.total_pages ?? 0,
        }));
      }
    } catch (error) {
      notify(apiMessage(error, "Could not load deals."));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, stageFilter, statusFilter, searchQuery, sort]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // New filters start again from the first page.
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [stageFilter, statusFilter, searchQuery, sort]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStageChange = async (projectId: string, stage: string, lostReason?: string) => {
    try {
      await put(`/deals/${projectId}/stage`, { stage_id: stage, lost_reason: lostReason ?? null });
      toast.success(`Deal moved to ${stages.find((s) => s.id === stage)?.name ?? "the new stage"}`);
      fetchProjects();
      setBoardKey((k) => k + 1);
    } catch (error) {
      notify(apiMessage(error, "Could not move the deal."));
    }
  };

  const handleBatchDelete = async (rows: { id: string }[]) => {
    if (!await confirmDialog(`Delete ${rows.length} selected deals? This cannot be undone.`)) return;

    const results = await Promise.allSettled(rows.map((r) => del(`/deals/${r.id}`)));
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setProjects((prev: any[]) => prev.filter((item) => !deleted.has(item.id)));
    const failed = rows.length - deleted.size;
    notify(failed ? `${deleted.size} deleted, ${failed} could not be deleted` : `${deleted.size} deals deleted`);
  };

  const handleDelete = async (projectId: string) => {
    if (!await confirmDialog("Are you sure you want to delete this deal?")) return;

    try {
      await del(`/deals/${projectId}`);
      setProjects(prev => prev.filter(project => project.id !== projectId));
      if (selectedProject?.id === projectId) {
        setShowProjectDrawer(false);
        setSelectedProject(null);
      }
      notify("Deal deleted");
    } catch (error) {
      notify(apiMessage(error, "Could not delete the deal."));
    }
  };

  const filteredProjects = projects; // search and filters are applied by the API


  const formatCurrency = (amount: number | string | null, currency: string = "USD") => {
    if (amount === null || amount === undefined) {
      return `${currency} 0`;
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) {
      return `${currency} 0`;
    }
    if (numAmount >= 1000000000) {
      return `${currency} ${(numAmount / 1000000000).toFixed(1)}B`;
    } else if (numAmount >= 1000000) {
      return `${currency} ${(numAmount / 1000000).toFixed(0)}M`;
    }
    return `${currency} ${numAmount.toLocaleString()}`;
  };

  const handleViewProject = (project: Project) => {
    setSelectedProject(project);
    setShowProjectDrawer(true);
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setShowEditProject(true);
  };

  const openBoardDeal = (deal: BoardDeal) => {
    setSelectedProject(fromApi(deal as unknown as ApiDeal));
    setShowProjectDrawer(true);
  };

  // Moving into a lost stage needs a reason, so it goes through the confirmation drawer.
  const moveBoardDeal = async (deal: BoardDeal, to: string) => {
    if (stages.find((st) => st.id === to)?.category === "lost") {
      setPendingMove({ id: deal.id, from: deal.stage_id, to });
      return false; // back to its column until the reason is given
    }
    await handleStageChange(deal.id, to);
  };

  const handleAddSuccess = () => {
    setBoardKey((k) => k + 1);
    fetchProjects();
    notify("Deal created");
  };

  const handleEditSuccess = () => {
    setBoardKey((k) => k + 1);
    fetchProjects();
    notify("Deal updated");
  };

  const columns: ColumnDef<Project>[] = [
    {
      accessorKey: "name",
      header: "Deal",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">{row.original.name}</p>
          <p className="text-xs text-gray-600">{row.original.client}</p>
        </div>
      ),
    },
    // {
    //   accessorKey: "type",
    //   header: "Type",
    //   cell: ({ row }) => (
    //     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
    //       {row.original.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
    //     </span>
    //   ),
    // },
    {
      accessorKey: "value",
      header: "Value",
      cell: ({ row }) => (
        <p className="text-xs font-semibold text-gray-900">
          {formatCurrency(row.original.value, row.original.currency)}{budgetSuffix(row.original.budget_frequency)}
        </p>
      ),
    },
    // {
    //   accessorKey: "status",
    //   header: "Status",
    //   cell: ({ row }) => (
    //     <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
    //       STATUS_COLORS[row.original.status as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-800'
    //     }`}>
    //       {row.original.status.replace('_', ' ').toUpperCase()}
    //     </span>
    //   ),
    // },
    {
      accessorKey: "current_stage",
      header: "Stage",
      cell: ({ row }) => {
        const stage = stages.find((s) => s.id === row.original.current_stage);
        return stage ? (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stage.color}`}>{stage.name}</span>
        ) : null;
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <div>
          <p className="text-xs text-gray-600">
            {new Date(row.original.created_at).toLocaleDateString()}
          </p>
          {row.original.owner_name && <p className="text-xs text-gray-500">owner: {row.original.owner_name}</p>}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          project={row.original}
          onView={() => handleViewProject(row.original)}
          onEdit={() => handleEditProject(row.original)}
          onDelete={() => handleDelete(row.original.id)}
        />
      ),
    },
  ];

  return (
    <StagesContext.Provider value={stages}>
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Deals" description="Move opportunities through your sales pipeline">
          <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs">
            {(["board", "list"] as const).map((v) => (
              <button key={v} type="button" onClick={() => chooseView(v)} className={`px-4 py-1 rounded-full transition ${view === v ? "bg-white shadow-sm text-gray-900 font-medium" : "text-gray-600"}`}>
                {v === "board" ? "Board" : "List"}
              </button>
            ))}
          </div>
          {view === "board" && (
            <button
              onClick={() => setShowAddProject(true)}
              className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200  min-w-[140px]"
            >
              Add Deal
            </button>
          )}
        </PageHeader>

        {view === "board" ? (
          <DealBoard refreshKey={boardKey} canMove={can("deals.update")} onOpen={openBoardDeal} onMove={moveBoardDeal} />
        ) : (
        <GridProvider id="deals">
        <div className="space-y-4">
        <GridToolbar
          search={<SearchInput className="w-full sm:w-64" placeholder="Search deal or account…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />}
          filters={<>
            <label className="text-xs text-gray-600">Status
              <AppSelect className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All deals</option><option value="open">Open</option><option value="won">Won</option><option value="lost">Lost</option>
              </AppSelect>
            </label>
            <label className="text-xs text-gray-600">Stage
              <AppSelect className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                <option value="all">Any stage</option>
                {stages.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
              </AppSelect>
            </label>
          </>}
          activeFilters={(statusFilter !== "all" ? 1 : 0) + (stageFilter !== "all" ? 1 : 0)}
          onClearFilters={() => { setStatusFilter("all"); setStageFilter("all"); }}
          sortOptions={[
            { value: "-created_at", label: "Newest" }, { value: "-amount", label: "Highest value" }, { value: "amount", label: "Lowest value" },
            { value: "expected_close_date", label: "Closing soonest" }, { value: "title", label: "Title" }, { value: "-stage_entered_at", label: "Recently moved" },
          ]}
          sort={sort}
          onSort={setSort}
          hasStats
          exportEntity="deals"
          exportPermission="deals.export"
          onAdd={() => setShowAddProject(true)}
          addLabel="Add Deal"
        />
        <DealStats refreshKey={boardKey} today={today} in30={in30} />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider columns={columns} data={filteredProjects} onDeleteSelected={handleBatchDelete}>
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead key={header.id} header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow key={row.id} row={row} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {filteredProjects.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FolderOpen className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">No deals yet</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery || statusFilter !== "all" || stageFilter !== "all" 
                      ? "Adjust your search criteria or filters to find relevant deals."
                      : "Add a deal, or convert a qualified lead into one."
                    }
                  </p>
                  {!searchQuery && statusFilter === "all" && stageFilter === "all" && (
                    <button
                      type="button"
                      onClick={() => setShowAddProject(true)}
                      className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                    >
                      Add First Deal
                    </button>
                  )}
                </div>
              )}

            </>
          )}
          <PaginationBar
            page={pagination.page}
            totalPages={pagination.total_pages}
            total={pagination.total}
            perPage={pagination.per_page}
            onPage={handlePageChange}
            onPerPage={(n) => setPagination((prev) => ({ ...prev, per_page: n, page: 1 }))}
            noun="deals"
          />
        </div>
        </div>
        </GridProvider>
        )}
      </div>

      <StageChangeModal
        isOpen={!!pendingMove}
        onClose={() => setPendingMove(null)}
        currentStage={pendingMove?.from ?? ""}
        newStage={pendingMove?.to ?? ""}
        onConfirm={(reason) => {
          if (pendingMove) handleStageChange(pendingMove.id, pendingMove.to, reason);
          setPendingMove(null);
        }}
      />

      <ProjectDrawer
        project={selectedProject}
        isOpen={showProjectDrawer}
        onClose={() => {
          setShowProjectDrawer(false);
          setSelectedProject(null);
        }}
        onDelete={handleDelete}
        onEdit={() => {
          setShowProjectDrawer(false);
          setShowEditProject(true);
        }}
        onStageChange={handleStageChange}
      />

      <AddProjectForm
        isOpen={showAddProject}
        onClose={() => setShowAddProject(false)}
        onSuccess={handleAddSuccess}
      />

      <EditProjectForm
        isOpen={showEditProject}
        onClose={() => {
          setShowEditProject(false);
          setSelectedProject(null);
        }}
        onSuccess={handleEditSuccess}
        project={selectedProject}
      />
    </div>
    </StagesContext.Provider>
  );
}

export default ProjectsPage;